import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { AccountInfo, Session, Comment, PendingComment, SessionCommentSummary } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useUserData } from '../contexts/UserDataContext';
import { encodeAccountId } from '../services/common';
import { usePostingQueuesSubscription } from '../hooks/usePostingQueuesSubscription';

interface SessionSidebarProps {
    isOpen: boolean;
    onToggle: () => void;
    currentSessionId?: string | null;
    currentAccountId?: string | null;
}

interface ExpandedSession extends Session {
    comments?: Comment[];
    commentCounts?: {
        total: number;
        selected: number;
        posted: number;
        pending: number;
    };
}

// Extended queue item to include account info for display
interface QueueItemWithAccount extends PendingComment {
    accountUrl: string;
    accountId: string;
}

// Derive comment counts from cached SessionCommentSummary[]
const deriveCommentCounts = (comments: SessionCommentSummary[]) => ({
    total: comments.length,
    selected: comments.filter(c => c.selected && !c.posted).length,
    posted: comments.filter(c => c.posted).length,
    pending: comments.filter(c => !c.selected && !c.posted && !c.declined).length,
});

// Helper to extract username from account URL
const extractUsername = (accountUrl: string) => {
    return accountUrl
        .replace(/^https?:\/\/(www\.)?instagram\.com\//, '')
        .replace(/\/$/, '');
};

const SessionSidebar: React.FC<SessionSidebarProps> = ({ isOpen, onToggle, currentSessionId, currentAccountId }) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { instagramAccountsTriggered: accounts, accountsLoading, contextLoading, refreshAccounts, isUsingRealtime: isAccountsRealtime, findSessionInContext, triggeredAccountsWithContext } = useUserData();
    const [expandedAccount, setExpandedAccount] = useState<string | null>(null);
    const [accountSessions, setAccountSessions] = useState<{ [key: string]: ExpandedSession[] }>({});
    const [sessionErrors, setSessionErrors] = useState<{ [key: string]: string }>({});
    const [loadingSessions, setLoadingSessions] = useState<string | null>(null);
    const [queueExpanded, setQueueExpanded] = useState(true);
    const [pollIntervalSeconds, setPollIntervalSeconds] = useState<number>(10); // Default 10 seconds
    const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const hasLoadedOnceRef = useRef<boolean>(false);
    const [currentAccountUrl, setCurrentAccountUrl] = useState<string | null>(null);
    const [accountResolved, setAccountResolved] = useState(false);

    // Cache sessionId -> accountUrl so switching between sessions of the same account skips the fetch
    const sessionAccountCacheRef = useRef<Map<string, string>>(new Map());

    // Use real-time posting queue subscription
    const {
        allQueueItems: realtimeQueueItems,
        isUsingRealtime: isQueueRealtime,
        loading: queueRealtimeLoading,
    } = usePostingQueuesSubscription(accounts, {
        vmPollInterval: 30000,  // 30s VM polling when queue running
        commentsLimit: 100,
    });

    // Fallback state for when real-time is not available
    const [fallbackQueueItems, setFallbackQueueItems] = useState<QueueItemWithAccount[]>([]);
    const [fallbackLoadingQueue, setFallbackLoadingQueue] = useState(true);

    // Determine which data source to use
    const allQueueItems = useMemo(() => {
        return isQueueRealtime ? realtimeQueueItems : fallbackQueueItems;
    }, [isQueueRealtime, realtimeQueueItems, fallbackQueueItems]);

    const loadingQueue = isQueueRealtime ? queueRealtimeLoading : fallbackLoadingQueue;

    // Fetch current session to get account URL when we have a sessionId
    // Uses UserContext cache → local ref cache → API fallback.
    useEffect(() => {
        const fetchCurrentSession = async () => {
            // Don't reset accountResolved to false - keep showing previous data while loading
            // This prevents a flash where queue items disappear during page transitions
            if (currentSessionId) {
                // Check local ref cache first
                const cached = sessionAccountCacheRef.current.get(currentSessionId);
                if (cached) {
                    setCurrentAccountUrl(cached);
                    setExpandedAccount((prev) => prev === cached ? prev : cached);
                    setAccountResolved(true);
                    return;
                }

                // Check UserContext cache (covers the 3 most recent sessions per account)
                const contextData = findSessionInContext(currentSessionId);
                if (contextData) {
                    const url = contextData.accountUrl;
                    sessionAccountCacheRef.current.set(currentSessionId, url);
                    setCurrentAccountUrl(url);
                    setExpandedAccount((prev) => prev === url ? prev : url);
                    setAccountResolved(true);
                    return;
                }

                // Fallback to API
                try {
                    const session = await api.getSession(currentSessionId);
                    sessionAccountCacheRef.current.set(currentSessionId, session.accountUrl);
                    setCurrentAccountUrl(session.accountUrl);
                    setExpandedAccount((prev) => prev === session.accountUrl ? prev : session.accountUrl);
                    setAccountResolved(true);
                } catch (err) {
                    console.error('Failed to fetch current session:', err);
                    setAccountResolved(true);
                }
            } else if (currentAccountId) {
                setCurrentAccountUrl(null);
                setAccountResolved(true);
            } else {
                setAccountResolved(true);
            }
        };
        fetchCurrentSession();
    }, [currentSessionId, currentAccountId, findSessionInContext]);

    // Refs for reading state inside the auto-expand effect without adding deps
    const accountSessionsRef = useRef(accountSessions);
    accountSessionsRef.current = accountSessions;
    const loadingSessionsRef = useRef(loadingSessions);
    loadingSessionsRef.current = loadingSessions;

    // Keep a ref to triggeredAccountsWithContext for reading in the effect
    const triggeredAccountsRef = useRef(triggeredAccountsWithContext);
    triggeredAccountsRef.current = triggeredAccountsWithContext;

    // Auto-fetch sessions when account is expanded (including auto-expansion)
    // Uses cached context for instant display; only hits API if no cache exists.
    useEffect(() => {
        if (!expandedAccount) return;
        if (loadingSessionsRef.current === expandedAccount) return;

        // If sessions already loaded (from previous expansion or context sync), skip
        if (accountSessionsRef.current[expandedAccount]) return;

        // Seed from UserContext cache for instant display
        const cachedAccount = triggeredAccountsRef.current.find(a => a.accountUrl === expandedAccount);
        const hasCachedSeed = !!(cachedAccount && cachedAccount.recentSessions.length > 0);

        if (hasCachedSeed) {
            const cachedSessions: ExpandedSession[] = cachedAccount!.recentSessions.map(s => {
                const session: ExpandedSession = {
                    id: s.id,
                    status: s.status || 'generating',
                    accountUrl: expandedAccount,
                    progress: s.progress,
                    createdAt: s.createdAt,
                    commentCounts: deriveCommentCounts(s.comments),
                };
                // Pre-populate session-to-account cache
                sessionAccountCacheRef.current.set(s.id, expandedAccount);
                return session;
            });

            setAccountSessions(prev => ({
                ...prev,
                [expandedAccount]: cachedSessions,
            }));
            // Cache is sufficient for instant display; the context sync effect below
            // keeps sidebar data fresh when polling/realtime updates arrive.
            return;
        }

        // No cached data available - fetch from API (foreground with loading spinner)
        fetchSessionsForAccount(expandedAccount);
    }, [expandedAccount]); // eslint-disable-line react-hooks/exhaustive-deps

    // Keep expanded account's sidebar data in sync with UserContext updates
    // (refreshed via polling or Firestore realtime listeners).
    useEffect(() => {
        if (!expandedAccount) return;
        const cachedAccount = triggeredAccountsWithContext.find(a => a.accountUrl === expandedAccount);
        if (!cachedAccount || cachedAccount.recentSessions.length === 0) return;

        const synced: ExpandedSession[] = cachedAccount.recentSessions.map(s => {
            sessionAccountCacheRef.current.set(s.id, expandedAccount);
            return {
                id: s.id,
                status: s.status || 'generating',
                accountUrl: expandedAccount,
                progress: s.progress,
                createdAt: s.createdAt,
                commentCounts: deriveCommentCounts(s.comments),
            };
        });

        setAccountSessions(prev => ({
            ...prev,
            [expandedAccount]: synced,
        }));
    }, [triggeredAccountsWithContext, expandedAccount]);

    // Fetch all posting queues for all accounts (fallback when real-time not available)
    // sync: if true, backend will sync selected comments to queue first (use only on first load)
    const fetchAllQueues = useCallback(async (accountList: AccountInfo[], sync: boolean = false) => {
        // Skip if using real-time listeners
        if (isQueueRealtime) {
            console.log('[SessionSidebar] Skipping fetchAllQueues - using real-time listeners');
            return;
        }

        setFallbackLoadingQueue(true);
        try {
            const queuePromises = accountList.map(async (account) => {
                const accountId = encodeAccountId(account.accountUrl);
                try {
                    const preview = await api.getPostingQueuePreview(accountId, 100);
                    if (preview && preview.totalPending > 0) {
                        return {
                            accountUrl: account.accountUrl,
                            accountId,
                            preview,
                        };
                    }
                } catch {
                    // No queue for this account
                }
                return null;
            });

            const results = await Promise.all(queuePromises);
            const validResults = results.filter((r): r is NonNullable<typeof r> => r !== null);

            // Flatten all queue items with account info
            const allItems: QueueItemWithAccount[] = [];
            for (const result of validResults) {
                for (const comment of result.preview.comments) {
                    allItems.push({
                        ...comment,
                        accountUrl: result.accountUrl,
                        accountId: result.accountId,
                    });
                }
            }

            // Smart update: preserve items during refresh to avoid flashing
            setFallbackQueueItems(prevItems => {
                // If no previous items, just set new items
                if (prevItems.length === 0) {
                    return allItems;
                }

                // Check if items actually changed (structural change)
                const prevIds = new Set(prevItems.map(i => i.id));
                const newIds = new Set(allItems.map(i => i.id));

                const hasStructuralChange =
                    prevIds.size !== newIds.size ||
                    Array.from(prevIds).some(id => !newIds.has(id)) ||
                    Array.from(newIds).some(id => !prevIds.has(id));

                if (hasStructuralChange) {
                    // Items added or removed - replace with new array
                    return allItems;
                }

                // Same items - merge status updates, preserving references where possible
                return allItems.map(newItem => {
                    const existing = prevItems.find(p => p.id === newItem.id);
                    // Preserve reference if nothing changed to avoid re-render
                    if (existing &&
                        existing.status === newItem.status &&
                        existing.content === newItem.content &&
                        existing.postUrl === newItem.postUrl) {
                        return existing;
                    }
                    return newItem;
                });
            });
        } catch (err) {
            console.error('Failed to fetch posting queues:', err);
        } finally {
            setFallbackLoadingQueue(false);
        }
    }, [isQueueRealtime]);

    // Fetch queues when accounts change (fallback only)
    const fetchQueuesForAccounts = useCallback(async () => {
        // Skip if using real-time listeners
        if (isQueueRealtime) {
            return;
        }
        if (accounts.length === 0 && !accountsLoading) {
            setFallbackLoadingQueue(false);
            return;
        }
        // Use sync=true only on first load, sync=false for subsequent polling
        const shouldSync = !hasLoadedOnceRef.current;
        await fetchAllQueues(accounts, shouldSync);
        hasLoadedOnceRef.current = true;
    }, [accounts, accountsLoading, fetchAllQueues, isQueueRealtime]);

    // Fetch frontend config on mount
    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const config = await api.getFrontendConfig();
                setPollIntervalSeconds(config.pollEverySecondsForPostingQueueOnSidePanel);
            } catch (err) {
                console.error('Failed to fetch frontend config, using default:', err);
            }
        };
        fetchConfig();
    }, []);

    // Fetch queues when accounts are loaded/changed (fallback only)
    useEffect(() => {
        // Skip if using real-time listeners - the hook handles everything
        if (isQueueRealtime) {
            return;
        }
        if (!accountsLoading) {
            fetchQueuesForAccounts();
        }
    }, [accounts, accountsLoading, fetchQueuesForAccounts, isQueueRealtime]);

    // Set up polling with configurable interval (only if not using real-time)
    useEffect(() => {
        // Clear any existing interval
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }

        // Skip polling if using Firestore real-time listeners for queues
        if (isQueueRealtime) {
            console.log('[SessionSidebar] Using real-time queue listeners, skipping polling');
            return;
        }

        // Also skip if accounts are using real-time (but queue isn't - edge case)
        if (isAccountsRealtime && !isQueueRealtime) {
            console.log('[SessionSidebar] Using real-time account listeners, using reduced polling for queues');
        }

        // Poll function: refresh accounts and queues
        const poll = async () => {
            if (!isAccountsRealtime) {
                await refreshAccounts();
            }
            // Note: fetchQueuesForAccounts will be triggered by the accounts useEffect above
        };

        console.log('[SessionSidebar] Setting up polling with interval:', pollIntervalSeconds, 'seconds');
        // Set up new interval with configured polling seconds
        pollIntervalRef.current = setInterval(poll, pollIntervalSeconds * 1000);
        return () => {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
            }
        };
    }, [refreshAccounts, pollIntervalSeconds, isAccountsRealtime, isQueueRealtime]);

    const fetchSessionsForAccount = async (accountUrl: string, background = false) => {
        if (!background) {
            setLoadingSessions(accountUrl);
        }
        // Clear any previous error for this account
        setSessionErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[accountUrl];
            return newErrors;
        });
        try {
            const sessions = await api.listSessionsForAccount(accountUrl);

            // Fetch comment counts for each session
            const sessionsWithCounts: ExpandedSession[] = await Promise.all(
                sessions.map(async (session) => {
                    try {
                        if (['awaiting_selection', 'posting', 'completed', 'needs_login'].includes(session.status)) {
                            const comments = await api.getComments(session.id);
                            return {
                                ...session,
                                comments,
                                commentCounts: {
                                    total: comments.length,
                                    selected: comments.filter((c) => c.selected && !c.posted).length,
                                    posted: comments.filter((c) => c.posted).length,
                                    pending: comments.filter((c) => !c.selected && !c.posted).length,
                                },
                            };
                        }
                    } catch (err) {
                        console.error(`Failed to fetch comments for session ${session.id}:`, err);
                    }
                    return session;
                })
            );

            // Pre-populate session-to-account cache so clicking any session in
            // the sidebar results in a synchronous cache hit, preventing the
            // async gap that can collapse the expanded account list.
            for (const s of sessionsWithCounts) {
                sessionAccountCacheRef.current.set(s.id, accountUrl);
            }

            setAccountSessions((prev) => ({
                ...prev,
                [accountUrl]: sessionsWithCounts,
            }));
        } catch (err) {
            console.error('Failed to fetch sessions:', err);
            // Only show error if this wasn't a background refresh (cache is already shown)
            if (!background) {
                const errorMessage = err instanceof Error ? err.message : 'Connection failed';
                setSessionErrors((prev) => ({
                    ...prev,
                    [accountUrl]: errorMessage,
                }));
            }
        } finally {
            if (!background) {
                setLoadingSessions(null);
            }
        }
    };

    const toggleAccount = (accountUrl: string) => {
        if (expandedAccount === accountUrl) {
            setExpandedAccount(null);
        } else {
            setExpandedAccount(accountUrl);
            // Let the useEffect handle seeding from cache and/or fetching from API.
            // Previously this also called fetchSessionsForAccount directly, causing
            // double API calls and a foreground loading spinner even when cached data existed.
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'completed':
                return '#10B981';  // Success green
            case 'awaiting_selection':
                return '#10B981';  // Success green (ready for review)
            case 'posting':
            case 'generating':
            case 'initializing':
                return '#7C3AED';  // Primary purple
            case 'error':
                return '#EF4444';  // Error red
            case 'needs_login':
                return '#F59E0B';  // Warning orange
            default:
                return '#6B7280';
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'completed':
                return 'Done';
            case 'awaiting_selection':
                return 'Review';
            case 'posting':
                return 'Posting';
            case 'generating':
                return 'Generating';
            case 'initializing':
                return 'Starting';
            case 'error':
                return 'Error';
            case 'needs_login':
                return 'Login';
            default:
                return status;
        }
    };

    const formatDate = (dateStr?: string) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const now = new Date();
        const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return 'Today';
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    // Show data once account resolution is complete
    const shouldShowData = accountResolved;

    // Always show all accounts in session history.
    // The expandedAccount state highlights/auto-expands the relevant one.
    const filteredAccounts: AccountInfo[] = (() => {
        if (!shouldShowData) return [];

        // If we have a currentAccountUrl that's not in the accounts list, add a placeholder
        if (currentAccountUrl) {
            const currentAccountId_ = encodeAccountId(currentAccountUrl);
            const exists = accounts.some(a => encodeAccountId(a.accountUrl) === currentAccountId_);
            if (!exists) {
                return [
                    ...accounts,
                    {
                        accountId: currentAccountId_,
                        accountUrl: currentAccountUrl,
                        sessionCount: 1,
                        lastActivity: new Date().toISOString(),
                    },
                ];
            }
        }

        return accounts;
    })();

    // Always show ALL queue items in the posting queue section (never filter by current page)
    const filteredQueueItems = shouldShowData ? allQueueItems : [];

    const filteredQueueCount = filteredQueueItems.length;

    // Group filtered queue items by account for the "Start Posting" buttons
    const queueByAccount = filteredQueueItems.reduce((acc, item) => {
        if (!acc[item.accountUrl]) {
            acc[item.accountUrl] = [];
        }
        acc[item.accountUrl].push(item);
        return acc;
    }, {} as { [key: string]: QueueItemWithAccount[] });

    if (!isOpen) {
        return (
            <button
                onClick={onToggle}
                style={{
                    position: 'fixed',
                    left: '16px',
                    top: '88px',
                    zIndex: 100,
                    background: '#000000',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '8px',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                }}
                title="Open session history"
            >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 12h18M3 6h18M3 18h18" />
                </svg>
            </button>
        );
    }

    return (
        <div
            style={{
                position: 'fixed',
                left: 0,
                top: '72px',
                bottom: 0,
                width: '320px',
                background: '#FFFFFF',
                borderRight: '1px solid #E5E5E5',
                zIndex: 99,
                display: 'flex',
                flexDirection: 'column',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
            }}
        >
            {/* Close Button Header */}
            <div
                style={{
                    padding: '12px 20px',
                    borderBottom: '1px solid #E5E5E5',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                }}
            >
                <button
                    onClick={onToggle}
                    style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        color: '#666666',
                    }}
                    title="Close sidebar"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                </button>
            </div>

            {/* Scrollable Content */}
            <div style={{ flex: 1, overflow: 'auto' }}>
                {/* ========== POSTING QUEUE SECTION ========== */}
                {/* Show queue section while loading (if accounts exist) or when there are items */}
                {(loadingQueue && accounts.length > 0) || filteredQueueCount > 0 ? (
                    <div style={{ padding: '12px', background: '#FFFFFF', borderBottom: '1px solid #E5E5E5' }}>
                        {/* Posting Queue Header */}
                        <button
                            onClick={() => setQueueExpanded(!queueExpanded)}
                            style={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '0 0 12px 0',
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#000000" strokeWidth="2">
                                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                                </svg>
                                <h2 style={{ fontSize: '14px', fontWeight: 600, color: '#000000', margin: 0 }}>
                                    Posting Queue
                                </h2>
                                <span
                                    style={{
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        padding: '2px 8px',
                                        borderRadius: '10px',
                                        background: '#000000',
                                        color: '#FFFFFF',
                                    }}
                                >
                                    {filteredQueueCount}
                                </span>
                            </div>
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#666666"
                                strokeWidth="2"
                                style={{
                                    transform: queueExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s',
                                }}
                            >
                                <polyline points="6,9 12,15 18,9" />
                            </svg>
                        </button>

                        {/* Queue Content (expanded) - compact account entries */}
                        {queueExpanded && (
                            <div>
                                {loadingQueue && allQueueItems.length === 0 ? (
                                    <div style={{ padding: '16px', textAlign: 'center', color: '#666666', fontSize: '13px' }}>
                                        Loading queue...
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        {Object.entries(queueByAccount).map(([accountUrl, items]) => {
                                            const acctId = items[0].accountId;
                                            const isActive = currentAccountId === acctId;
                                            return (
                                                <button
                                                    key={accountUrl}
                                                    onClick={() => navigate(`/queue/${acctId}`)}
                                                    style={{
                                                        width: '100%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                        padding: '8px 10px',
                                                        background: isActive ? '#F0F0F0' : '#FFFFFF',
                                                        border: isActive ? '2px solid #000000' : '1px solid #E5E5E5',
                                                        borderRadius: '6px',
                                                        cursor: 'pointer',
                                                        transition: 'background 0.2s',
                                                        textAlign: 'left',
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            width: '24px',
                                                            height: '24px',
                                                            borderRadius: '50%',
                                                            background: '#000000',
                                                            color: '#FFFFFF',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontSize: '11px',
                                                            fontWeight: 600,
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {extractUsername(accountUrl).charAt(0).toUpperCase()}
                                                    </div>
                                                    <span style={{ fontSize: '12px', fontWeight: 600, color: '#000000', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        @{extractUsername(accountUrl)}
                                                    </span>
                                                    <span
                                                        style={{
                                                            fontSize: '11px',
                                                            fontWeight: 600,
                                                            padding: '2px 8px',
                                                            borderRadius: '10px',
                                                            background: '#000000',
                                                            color: '#FFFFFF',
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {items.length}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ) : null}

                {/* ========== SESSION HISTORY SECTION ========== */}
                <div style={{ padding: '12px' }}>
                    {/* Session History Header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '0 0 12px 0', padding: '0 4px' }}>
                        <h2 style={{ fontSize: '14px', fontWeight: 600, color: '#000000', margin: 0 }}>
                            Session History
                        </h2>
                        {contextLoading && !accountsLoading && (
                            <div
                                style={{
                                    width: '14px',
                                    height: '14px',
                                    border: '2px solid #E5E5E5',
                                    borderTop: '2px solid #7C3AED',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite',
                                    flexShrink: 0,
                                }}
                                title="Loading session details..."
                            />
                        )}
                    </div>

                    {/* Accounts List */}
                {(accountsLoading || !accountResolved) ? (
                    <div style={{ textAlign: 'center', padding: '40px 20px', color: '#666666' }}>
                        <div
                            style={{
                                width: '24px',
                                height: '24px',
                                border: '2px solid #E5E5E5',
                                borderTop: '2px solid #000000',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite',
                                margin: '0 auto 12px',
                            }}
                        />
                        Loading accounts...
                    </div>
                ) : filteredAccounts.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 20px', color: '#666666' }}>
                        <p style={{ margin: 0 }}>No sessions yet</p>
                        <p style={{ margin: '8px 0 0', fontSize: '13px' }}>
                            Create a session from the home page
                        </p>
                    </div>
                ) : (
                    filteredAccounts.map((account) => (
                        <div key={account.accountId} style={{ marginBottom: '8px' }}>
                            {/* Account Header */}
                            <button
                                onClick={() => toggleAccount(account.accountUrl)}
                                style={{
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '12px 16px',
                                    background: expandedAccount === account.accountUrl ? '#F7F7F7' : '#FFFFFF',
                                    border: '1px solid #E5E5E5',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    transition: 'background 0.2s',
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div
                                        style={{
                                            width: '32px',
                                            height: '32px',
                                            borderRadius: '50%',
                                            background: '#000000',
                                            color: '#FFFFFF',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '14px',
                                            fontWeight: 600,
                                        }}
                                    >
                                        {extractUsername(account.accountUrl).charAt(0).toUpperCase()}
                                    </div>
                                    <div style={{ textAlign: 'left' }}>
                                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#000000' }}>
                                            @{extractUsername(account.accountUrl)}
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#666666' }}>
                                            {account.sessionCount} session{account.sessionCount !== 1 ? 's' : ''}
                                        </div>
                                    </div>
                                </div>
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="#666666"
                                    strokeWidth="2"
                                    style={{
                                        transform: expandedAccount === account.accountUrl ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.2s',
                                    }}
                                >
                                    <polyline points="6,9 12,15 18,9" />
                                </svg>
                            </button>

                            {/* Sessions List (expanded) */}
                            {expandedAccount === account.accountUrl && (
                                <div style={{ marginTop: '4px', paddingLeft: '8px' }}>
                                    {loadingSessions === account.accountUrl ? (
                                        <div style={{ padding: '16px', textAlign: 'center', color: '#666666', fontSize: '13px' }}>
                                            Loading sessions...
                                        </div>
                                    ) : sessionErrors[account.accountUrl] ? (
                                        <div style={{ padding: '16px', textAlign: 'center', fontSize: '13px' }}>
                                            <div style={{ color: '#EF4444', marginBottom: '8px' }}>
                                                Failed to load sessions
                                            </div>
                                            <div style={{ color: '#999999', fontSize: '11px', marginBottom: '8px' }}>
                                                {sessionErrors[account.accountUrl]}
                                            </div>
                                            <button
                                                onClick={() => fetchSessionsForAccount(account.accountUrl)}
                                                style={{
                                                    padding: '6px 12px',
                                                    fontSize: '12px',
                                                    background: '#F3F4F6',
                                                    border: '1px solid #D1D5DB',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    color: '#374151',
                                                }}
                                            >
                                                Retry
                                            </button>
                                        </div>
                                    ) : accountSessions[account.accountUrl]?.length > 0 ? (
                                        accountSessions[account.accountUrl].map((session) => (
                                                    <button
                                                        key={session.id}
                                                        onClick={() => navigate(`/session/${session.id}`)}
                                                        style={{
                                                            width: '100%',
                                                            display: 'block',
                                                            padding: '12px 14px',
                                                            marginBottom: '4px',
                                                            background: currentSessionId === session.id ? '#F0F0F0' : '#FAFAFA',
                                                            border: currentSessionId === session.id ? '2px solid #000000' : '1px solid #E5E5E5',
                                                            borderRadius: '6px',
                                                            cursor: 'pointer',
                                                            textAlign: 'left',
                                                            transition: 'all 0.2s',
                                                        }}
                                                    >
                                                        {/* Session date and status */}
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                                            <span style={{ fontSize: '12px', color: '#666666' }}>
                                                                {formatDate(session.createdAt)}
                                                            </span>
                                                            <span
                                                                style={{
                                                                    fontSize: '10px',
                                                                    fontWeight: 600,
                                                                    padding: '2px 8px',
                                                                    borderRadius: '4px',
                                                                    background: `${getStatusColor(session.status)}20`,
                                                                    color: getStatusColor(session.status),
                                                                    textTransform: 'uppercase',
                                                                }}
                                                            >
                                                                {getStatusLabel(session.status)}
                                                            </span>
                                                        </div>

                                                        {/* Comment counts */}
                                                        {session.commentCounts && (
                                                            <div style={{ display: 'flex', gap: '12px', fontSize: '11px' }}>
                                                                <span style={{ color: '#000000' }}>
                                                                    {session.commentCounts.total} total
                                                                </span>
                                                                <span style={{ color: '#10B981' }}>
                                                                    {session.commentCounts.selected} ready
                                                                </span>
                                                                <span style={{ color: '#3B82F6' }}>
                                                                    {session.commentCounts.posted} posted
                                                                </span>
                                                            </div>
                                                        )}

                                                        {/* Progress for active sessions and errors */}
                                                        {['generating', 'initializing', 'posting', 'error'].includes(session.status) && session.progress && (
                                                            <div style={{ fontSize: '11px', color: session.status === 'error' ? '#EF4444' : '#666666', marginTop: '4px' }}>
                                                                {session.progress.length > 40
                                                                    ? session.progress.substring(0, 40) + '...'
                                                                    : session.progress}
                                                            </div>
                                                        )}
                                            </button>
                                        ))
                                    ) : (
                                        <div style={{ padding: '16px', textAlign: 'center', color: '#666666', fontSize: '13px' }}>
                                            No sessions found
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))
                )}
                </div>
            </div>

            {/* Footer - Dashboard + New Session Buttons */}
            <div style={{ padding: '12px', borderTop: '1px solid #E5E5E5', display: 'flex', gap: '8px' }}>
                <button
                    onClick={() => {
                        if (!user) {
                            navigate(`/login?redirect=${encodeURIComponent('/sessions')}`);
                        } else {
                            navigate('/sessions');
                        }
                    }}
                    style={{
                        flex: 1,
                        padding: '12px 0',
                        background: '#F3F4F6',
                        color: '#333333',
                        border: '1px solid #E5E5E5',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                    }}
                >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                        <polyline points="9 22 9 12 15 12 15 22" />
                    </svg>
                    Dashboard
                </button>
                <button
                    onClick={() => {
                        if (!user) {
                            navigate(`/login?redirect=${encodeURIComponent('/sessions')}`);
                        } else {
                            navigate('/sessions');
                        }
                    }}
                    style={{
                        flex: 1,
                        padding: '12px 0',
                        background: '#7C3AED',
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                    }}
                >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 5v14M5 12h14" />
                    </svg>
                    New Session
                </button>
            </div>

            <style>
                {`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}
            </style>
        </div>
    );
};

export default SessionSidebar;
